import React, { useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import { errorAlert, successAlert } from "../../utils/alert";

const AUDIENCES = [
  { key: "players", label: "Players" },
  { key: "escorts", label: "Escorts" },
  { key: "volunteers", label: "Volunteers" },
];

const defaultSettings = {
  is_enabled: true,
  mailer: "smtp",
  smtp_host: "",
  smtp_port: 587,
  smtp_username: "",
  smtp_password: "",
  has_smtp_password: false,
  smtp_encryption: "tls",
  from_address: "",
  from_name: "",
  available_from_addresses: "",
  reply_to_address: "",
  reply_to_name: "",
  admin_notification_emails: "",
};

const emptyTemplateState = {
  key: "",
  name: "",
  subject: "",
  body: "",
  is_enabled: true,
  available_variables: [],
};

const MailCenter = () => {
  const { role } = useContext(AuthContext);
  const normalizedRole = (role || "").toLowerCase();

  const [settings, setSettings] = useState(defaultSettings);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
  const [templateForm, setTemplateForm] = useState(emptyTemplateState);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateResetting, setTemplateResetting] = useState(false);

  const [audience, setAudience] = useState("players");
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [search, setSearch] = useState("");

  const [compose, setCompose] = useState({
    send_mode: "selected",
    from_address: "",
    from_name: "",
    additional_emails: "",
    subject: "",
    body: "",
    cc_admins: true,
  });
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const filteredRecipients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter((row) => {
      const haystack = [
        row?.name,
        row?.email,
        row?.player_name,
        row?.event_title,
      ]
        .map((item) => String(item || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [recipients, search]);

  const availableFromAddresses = useMemo(() => {
    const base = [];
    const primary = String(settings?.from_address || "").trim().toLowerCase();
    if (primary) base.push(primary);

    const extra = String(settings?.available_from_addresses || "")
      .split(/[,\n;]+/)
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);

    return Array.from(new Set([...base, ...extra]));
  }, [settings?.from_address, settings?.available_from_addresses]);

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const allFilteredSelected =
    filteredRecipients.length > 0 &&
    filteredRecipients.every((row) => selectedSet.has(row.key));

  useEffect(() => {
    if (!message.text) return;
    if (message.type === "success") {
      successAlert("Success", message.text);
      return;
    }
    if (message.type === "error") {
      errorAlert("Error", message.text);
    }
  }, [message]);

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      setSettingsLoading(true);
      try {
        const data = await API.fetchAdminMailSettings();
        if (!active) return;
        setSettings({
          ...defaultSettings,
          ...data,
          available_from_addresses: Array.isArray(data?.available_from_addresses)
            ? data.available_from_addresses.join(", ")
            : "",
          admin_notification_emails: Array.isArray(data?.admin_notification_emails)
            ? data.admin_notification_emails.join(", ")
            : "",
        });
      } catch (err) {
        if (!active) return;
        setMessage({
          type: "error",
          text: err?.message || "Unable to load mail settings.",
        });
      } finally {
        if (active) setSettingsLoading(false);
      }
    };

    loadSettings();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const data = await API.fetchAdminMailTemplates();
        if (!active) return;
        const rows = Array.isArray(data) ? data : [];
        setTemplates(rows);
        if (!rows.length) {
          setSelectedTemplateKey("");
          setTemplateForm(emptyTemplateState);
          return;
        }

        const selected = rows[0];
        setSelectedTemplateKey(selected.key || "");
        setTemplateForm({
          key: selected.key || "",
          name: selected.name || "",
          subject: selected.subject || "",
          body: selected.body || "",
          is_enabled: !!selected.is_enabled,
          available_variables: Array.isArray(selected.available_variables)
            ? selected.available_variables
            : [],
        });
      } catch (err) {
        if (!active) return;
        setMessage({
          type: "error",
          text: err?.message || "Unable to load workflow mail templates.",
        });
      } finally {
        if (active) setTemplatesLoading(false);
      }
    };

    loadTemplates();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const selected = templates.find((row) => row.key === selectedTemplateKey);
    if (!selected) return;
    setTemplateForm({
      key: selected.key || "",
      name: selected.name || "",
      subject: selected.subject || "",
      body: selected.body || "",
      is_enabled: !!selected.is_enabled,
      available_variables: Array.isArray(selected.available_variables)
        ? selected.available_variables
        : [],
    });
  }, [selectedTemplateKey, templates]);

  useEffect(() => {
    const primary = String(settings?.from_address || "").trim().toLowerCase();
    const fallback = availableFromAddresses[0] || "";
    const target = primary || fallback;
    if (!target) return;

    setCompose((prev) => {
      const current = String(prev.from_address || "").trim().toLowerCase();
      if (current && availableFromAddresses.includes(current)) return prev;
      return { ...prev, from_address: target };
    });
  }, [settings?.from_address, availableFromAddresses]);

  useEffect(() => {
    let active = true;
    const loadRecipients = async () => {
      setRecipientLoading(true);
      setSelectedKeys([]);
      try {
        const data = await API.fetchAdminMailRecipients(audience);
        if (!active) return;
        setRecipients(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        setRecipients([]);
        setMessage({
          type: "error",
          text: err?.message || "Unable to load recipients.",
        });
      } finally {
        if (active) setRecipientLoading(false);
      }
    };
    loadRecipients();
    return () => {
      active = false;
    };
  }, [audience]);

  if (!["admin", "secretary"].includes(normalizedRole)) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  const updateSettingsField = (name, value) => {
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const updateTemplateField = (name, value) => {
    setTemplateForm((prev) => ({ ...prev, [name]: value }));
  };

  const selectTemplate = (key) => {
    setSelectedTemplateKey(key);
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSettingsSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        is_enabled: !!settings.is_enabled,
        mailer: settings.mailer || "smtp",
        smtp_host: settings.smtp_host || "",
        smtp_port: settings.smtp_port ? Number(settings.smtp_port) : null,
        smtp_username: settings.smtp_username || "",
        smtp_password: settings.smtp_password || "",
        smtp_encryption: settings.smtp_encryption || "",
        from_address: settings.from_address || "",
        from_name: settings.from_name || "",
        available_from_addresses: String(settings.available_from_addresses || "")
          .split(/[,\n;]+/)
          .map((item) => item.trim())
          .filter(Boolean),
        reply_to_address: settings.reply_to_address || "",
        reply_to_name: settings.reply_to_name || "",
        admin_notification_emails: String(settings.admin_notification_emails || "")
          .split(/[,\n;]+/)
          .map((item) => item.trim())
          .filter(Boolean),
      };

      const res = await API.updateAdminMailSettings(payload);
      const data = res?.data || res || {};
      setSettings((prev) => ({
        ...prev,
        ...data,
        smtp_password: "",
        has_smtp_password: !!data?.has_smtp_password,
        admin_notification_emails: Array.isArray(data?.admin_notification_emails)
          ? data.admin_notification_emails.join(", ")
          : prev.admin_notification_emails,
        available_from_addresses: Array.isArray(data?.available_from_addresses)
          ? data.available_from_addresses.join(", ")
          : prev.available_from_addresses,
      }));
      setMessage({ type: "success", text: "Mail settings saved." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Unable to save mail settings.",
      });
    } finally {
      setSettingsSaving(false);
    }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.key) return;

    setTemplateSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await API.updateAdminMailTemplate(templateForm.key, {
        subject: templateForm.subject,
        body: templateForm.body,
        is_enabled: !!templateForm.is_enabled,
      });
      const updated = res?.data || res || {};
      setTemplates((prev) =>
        prev.map((row) => (row.key === updated.key ? updated : row))
      );
      setTemplateForm((prev) => ({
        ...prev,
        subject: updated.subject || "",
        body: updated.body || "",
        is_enabled: !!updated.is_enabled,
        available_variables: Array.isArray(updated.available_variables)
          ? updated.available_variables
          : [],
      }));
      setMessage({ type: "success", text: "Mail template saved." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Unable to save mail template.",
      });
    } finally {
      setTemplateSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!templateForm.key) return;
    if (!window.confirm(`Reset "${templateForm.name}" to its default content?`)) {
      return;
    }

    setTemplateResetting(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await API.resetAdminMailTemplate(templateForm.key);
      const updated = res?.data || res || {};
      setTemplates((prev) =>
        prev.map((row) => (row.key === updated.key ? updated : row))
      );
      setTemplateForm({
        key: updated.key || "",
        name: updated.name || "",
        subject: updated.subject || "",
        body: updated.body || "",
        is_enabled: !!updated.is_enabled,
        available_variables: Array.isArray(updated.available_variables)
          ? updated.available_variables
          : [],
      });
      setMessage({ type: "success", text: "Mail template reset to default." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Unable to reset mail template.",
      });
    } finally {
      setTemplateResetting(false);
    }
  };

  const toggleRecipient = (key) => {
    setSelectedKeys((prev) => {
      if (prev.includes(key)) return prev.filter((item) => item !== key);
      return [...prev, key];
    });
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedKeys((prev) =>
        prev.filter((key) => !filteredRecipients.some((row) => row.key === key))
      );
      return;
    }
    const next = new Set(selectedKeys);
    filteredRecipients.forEach((row) => next.add(row.key));
    setSelectedKeys(Array.from(next));
  };

  const sendMail = async (e) => {
    e.preventDefault();
    setSending(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        audience,
        send_mode: compose.send_mode,
        recipient_keys: compose.send_mode === "selected" ? selectedKeys : [],
        from_address: compose.from_address || "",
        from_name: compose.from_name || "",
        additional_emails: compose.additional_emails,
        subject: compose.subject,
        body: compose.body,
        cc_admins: !!compose.cc_admins,
      };

      const res = await API.sendAdminMail(payload);
      const data = res?.data || {};
      const firstError =
        Array.isArray(data?.errors) && data.errors.length > 0
          ? String(data.errors[0]?.error || "").trim()
          : "";
      const effectiveFrom = String(
        data?.effective_from_address || payload.from_address || ""
      ).trim();
      setMessage({
        type: "success",
        text: `Mail processed. Sent: ${data?.sent || 0}, Failed: ${data?.failed || 0}${effectiveFrom ? `, From: ${effectiveFrom}` : ""}${firstError ? `. First error: ${firstError}` : ""}`,
      });
      setCompose((prev) => ({
        ...prev,
        subject: "",
        body: "",
        additional_emails: "",
      }));
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Unable to send mail.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-slate-50 py-8 px-4">
        <div className="max-w-7xl mx-auto space-y-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Mail Center</h1>

          <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">SMTP Settings</h2>
            {settingsLoading ? (
              <p className="text-slate-500">Loading settings...</p>
            ) : (
              <form onSubmit={saveSettings} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 md:col-span-2">
                  <input
                    type="checkbox"
                    checked={!!settings.is_enabled}
                    onChange={(e) => updateSettingsField("is_enabled", e.target.checked)}
                  />
                  Enable email sending
                </label>

                <input
                  type="text"
                  value={settings.mailer || ""}
                  onChange={(e) => updateSettingsField("mailer", e.target.value)}
                  placeholder="Mailer (smtp)"
                  className="border rounded px-3 py-2"
                />
                <input
                  type="text"
                  value={settings.smtp_host || ""}
                  onChange={(e) => updateSettingsField("smtp_host", e.target.value)}
                  placeholder="SMTP Host"
                  className="border rounded px-3 py-2"
                />
                <input
                  type="number"
                  value={settings.smtp_port || ""}
                  onChange={(e) => updateSettingsField("smtp_port", e.target.value)}
                  placeholder="SMTP Port"
                  className="border rounded px-3 py-2"
                />
                <input
                  type="text"
                  value={settings.smtp_encryption || ""}
                  onChange={(e) => updateSettingsField("smtp_encryption", e.target.value)}
                  placeholder="Encryption (tls/ssl)"
                  className="border rounded px-3 py-2"
                />
                <input
                  type="text"
                  value={settings.smtp_username || ""}
                  onChange={(e) => updateSettingsField("smtp_username", e.target.value)}
                  placeholder="SMTP Username"
                  className="border rounded px-3 py-2"
                />
                <input
                  type="password"
                  value={settings.smtp_password || ""}
                  onChange={(e) => updateSettingsField("smtp_password", e.target.value)}
                  placeholder={
                    settings.has_smtp_password
                      ? "Leave blank to keep existing password"
                      : "SMTP Password"
                  }
                  className="border rounded px-3 py-2"
                />
                <input
                  type="email"
                  value={settings.from_address || ""}
                  onChange={(e) => updateSettingsField("from_address", e.target.value)}
                  placeholder="From Email"
                  className="border rounded px-3 py-2"
                />
                <input
                  type="text"
                  value={settings.from_name || ""}
                  onChange={(e) => updateSettingsField("from_name", e.target.value)}
                  placeholder="From Name"
                  className="border rounded px-3 py-2"
                />
                <textarea
                  value={settings.available_from_addresses || ""}
                  onChange={(e) => updateSettingsField("available_from_addresses", e.target.value)}
                  placeholder="Allowed From Addresses (comma/newline separated)"
                  className="border rounded px-3 py-2 md:col-span-2 min-h-[84px]"
                />
                <input
                  type="email"
                  value={settings.reply_to_address || ""}
                  onChange={(e) => updateSettingsField("reply_to_address", e.target.value)}
                  placeholder="Reply-To Email"
                  className="border rounded px-3 py-2"
                />
                <input
                  type="text"
                  value={settings.reply_to_name || ""}
                  onChange={(e) => updateSettingsField("reply_to_name", e.target.value)}
                  placeholder="Reply-To Name"
                  className="border rounded px-3 py-2"
                />
                <textarea
                  value={settings.admin_notification_emails || ""}
                  onChange={(e) => updateSettingsField("admin_notification_emails", e.target.value)}
                  placeholder="Admin notification emails (comma/newline separated)"
                  className="border rounded px-3 py-2 md:col-span-2 min-h-[90px]"
                />

                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={settingsSaving}
                    className={`px-4 py-2 rounded text-white ${
                      settingsSaving
                        ? "bg-slate-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {settingsSaving ? "Saving..." : "Save Mail Settings"}
                  </button>
                </div>
              </form>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
            <div className="flex flex-col gap-1 mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Workflow Templates</h2>
              <p className="text-sm text-slate-600">
                Edit the automatic emails used for OTPs, approvals, registrations, ID-card delivery and certificate delivery.
              </p>
            </div>

            {templatesLoading ? (
              <p className="text-slate-500">Loading templates...</p>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)] gap-4">
                <div className="border border-slate-200 rounded-lg p-3 space-y-2 max-h-[560px] overflow-auto">
                  {templates.map((template) => {
                    const active = template.key === selectedTemplateKey;
                    return (
                      <button
                        key={template.key}
                        type="button"
                        onClick={() => selectTemplate(template.key)}
                        className={`w-full text-left border rounded-lg px-3 py-3 transition-colors ${
                          active
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-slate-900">{template.name}</p>
                          <span
                            className={`text-[11px] px-2 py-0.5 rounded-full ${
                              template.is_enabled
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-200 text-slate-600"
                            }`}
                          >
                            {template.is_enabled ? "Enabled" : "Disabled"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{template.key}</p>
                      </button>
                    );
                  })}
                  {!templates.length && (
                    <p className="text-sm text-slate-500">No templates found.</p>
                  )}
                </div>

                <div className="border border-slate-200 rounded-lg p-4">
                  {!templateForm.key ? (
                    <p className="text-slate-500">Select a template to edit.</p>
                  ) : (
                    <form onSubmit={saveTemplate} className="space-y-4">
                      <div className="flex flex-col gap-1">
                        <h3 className="text-lg font-semibold text-slate-900">{templateForm.name}</h3>
                        <p className="text-xs text-slate-500">{templateForm.key}</p>
                      </div>

                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={!!templateForm.is_enabled}
                          onChange={(e) => updateTemplateField("is_enabled", e.target.checked)}
                        />
                        Enable this automatic email
                      </label>

                      <label className="block text-sm font-medium text-slate-700">
                        Subject
                        <input
                          type="text"
                          value={templateForm.subject}
                          onChange={(e) => updateTemplateField("subject", e.target.value)}
                          className="mt-1 w-full border rounded px-3 py-2"
                          required
                        />
                      </label>

                      <label className="block text-sm font-medium text-slate-700">
                        Body
                        <textarea
                          value={templateForm.body}
                          onChange={(e) => updateTemplateField("body", e.target.value)}
                          className="mt-1 w-full border rounded px-3 py-2 min-h-[280px]"
                          required
                        />
                      </label>

                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Available Variables</p>
                        <div className="flex flex-wrap gap-2">
                          {(templateForm.available_variables || []).map((item) => (
                            <code
                              key={item}
                              className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700"
                            >
                              {"{{"}{item}{"}}"}
                            </code>
                          ))}
                          {!templateForm.available_variables?.length && (
                            <p className="text-sm text-slate-500">No variables for this template.</p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          type="submit"
                          disabled={templateSaving}
                          className={`px-4 py-2 rounded text-white ${
                            templateSaving
                              ? "bg-slate-400 cursor-not-allowed"
                              : "bg-blue-600 hover:bg-blue-700"
                          }`}
                        >
                          {templateSaving ? "Saving..." : "Save Template"}
                        </button>
                        <button
                          type="button"
                          onClick={resetTemplate}
                          disabled={templateResetting}
                          className={`px-4 py-2 rounded border ${
                            templateResetting
                              ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                              : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {templateResetting ? "Resetting..." : "Reset to Default"}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Send Mail</h2>

            <div className="flex flex-wrap gap-2 mb-4">
              {AUDIENCES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setAudience(item.key)}
                  className={`px-3 py-1 rounded border text-sm ${
                    audience === item.key
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white border-slate-300 text-slate-700"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search recipients"
                    className="flex-1 border rounded px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={toggleSelectAllFiltered}
                    className="px-3 py-2 text-sm rounded border border-slate-300 hover:bg-slate-100"
                  >
                    {allFilteredSelected ? "Unselect" : "Select"} filtered
                  </button>
                </div>

                {recipientLoading ? (
                  <p className="text-sm text-slate-500">Loading recipients...</p>
                ) : (
                  <div className="max-h-[360px] overflow-auto space-y-2">
                    {filteredRecipients.map((row) => (
                      <label
                        key={row.key}
                        className="flex items-start gap-2 border border-slate-200 rounded p-2"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSet.has(row.key)}
                          onChange={() => toggleRecipient(row.key)}
                        />
                        <div className="text-sm">
                          <p className="font-medium text-slate-900">{row.name || "Recipient"}</p>
                          <p className="text-slate-600">{row.email}</p>
                          {row.player_name && (
                            <p className="text-xs text-slate-500">Player: {row.player_name}</p>
                          )}
                          {row.event_title && (
                            <p className="text-xs text-slate-500">Event: {row.event_title}</p>
                          )}
                        </div>
                      </label>
                    ))}
                    {!filteredRecipients.length && (
                      <p className="text-sm text-slate-500">No recipients found.</p>
                    )}
                  </div>
                )}
              </div>

              <form onSubmit={sendMail} className="border border-slate-200 rounded-lg p-3 space-y-3">
                <label className="block text-sm font-medium text-slate-700">
                  Send Mode
                  <select
                    value={compose.send_mode}
                    onChange={(e) => setCompose((prev) => ({ ...prev, send_mode: e.target.value }))}
                    className="mt-1 w-full border rounded px-3 py-2"
                  >
                    <option value="selected">Selected recipients</option>
                    <option value="all">All recipients in this audience</option>
                  </select>
                </label>

                <label className="block text-sm font-medium text-slate-700">
                  From Address
                  <select
                    value={compose.from_address}
                    onChange={(e) =>
                      setCompose((prev) => ({ ...prev, from_address: e.target.value }))
                    }
                    required
                    className="mt-1 w-full border rounded px-3 py-2"
                  >
                    {!availableFromAddresses.length && (
                      <option value="">No allowed from address configured</option>
                    )}
                    {availableFromAddresses.map((email) => (
                      <option key={email} value={email}>
                        {email}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block text-sm font-medium text-slate-700">
                  From Name (optional override)
                  <input
                    type="text"
                    value={compose.from_name}
                    onChange={(e) => setCompose((prev) => ({ ...prev, from_name: e.target.value }))}
                    className="mt-1 w-full border rounded px-3 py-2"
                    placeholder={settings.from_name || "Default from name"}
                  />
                </label>

                <label className="block text-sm font-medium text-slate-700">
                  Additional Emails
                  <textarea
                    value={compose.additional_emails}
                    onChange={(e) =>
                      setCompose((prev) => ({ ...prev, additional_emails: e.target.value }))
                    }
                    className="mt-1 w-full border rounded px-3 py-2 min-h-[84px]"
                    placeholder="Use comma or new line. Example: person1@mail.com, person2@mail.com"
                  />
                </label>

                <label className="block text-sm font-medium text-slate-700">
                  Subject
                  <input
                    type="text"
                    value={compose.subject}
                    onChange={(e) => setCompose((prev) => ({ ...prev, subject: e.target.value }))}
                    className="mt-1 w-full border rounded px-3 py-2"
                    required
                  />
                </label>

                <label className="block text-sm font-medium text-slate-700">
                  Message
                  <textarea
                    value={compose.body}
                    onChange={(e) => setCompose((prev) => ({ ...prev, body: e.target.value }))}
                    className="mt-1 w-full border rounded px-3 py-2 min-h-[160px]"
                    required
                  />
                </label>

                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!compose.cc_admins}
                    onChange={(e) => setCompose((prev) => ({ ...prev, cc_admins: e.target.checked }))}
                  />
                  Include configured admin notification emails
                </label>

                <button
                  type="submit"
                  disabled={sending}
                  className={`px-4 py-2 rounded text-white ${
                    sending ? "bg-slate-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  {sending ? "Sending..." : "Send Mail"}
                </button>
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default MailCenter;
